import { ReactNode } from 'react';

import { Member } from './types';

/** Encoded @mention token in a stored comment body: @[Display Name](member-uuid). The input inserts
 *  these; the backend parses the ids to notify; the display strips them to a highlighted @name. */
export const MENTION_RE = /@\[([^\]]*)\]\(([0-9a-fA-F-]{36})\)/g;

/** Serialize a mention for storage. */
export const encodeMention = (m: Member): string => `@[${m.name}](${m.id})`;

/** Render a comment body: plain text with @mentions shown as highlighted chips (the current member's
 *  own mentions get a stronger tint). Falls back to the encoded name if the member is unknown now. */
export function renderCommentBody(body: string, memberById: Map<string, Member>, meId?: string | null): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  const re = new RegExp(MENTION_RE);
  let i = 0;
  while ((m = re.exec(body)) !== null) {
    if (m.index > last) out.push(body.slice(last, m.index));
    const id = m[2];
    const name = memberById.get(id)?.name ?? m[1];
    const isMe = !!meId && id === meId;
    out.push(
      <span
        key={`m${i++}`}
        className={isMe ? 'rounded bg-accent/20 px-1 font-medium text-accent' : 'rounded bg-accent/10 px-1 font-medium text-accent'}
      >
        @{name}
      </span>,
    );
    last = m.index + m[0].length;
  }
  if (last < body.length) out.push(body.slice(last));
  return out;
}

/** Strip mention tokens to plain "@name" text (for notification snippets, email, etc.). */
export const stripMentions = (body: string): string => body.replace(MENTION_RE, (_s, name) => `@${name}`);
